import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
    Loader2, 
    Users, 
    MapPin, 
    FileText, 
    AlertTriangle, 
    RefreshCw,
    Trash2,
    CheckCircle,
    ArrowRight,
    TrendingUp,
    Clock,
    Eye
} from 'lucide-react';
import { adminAPI, reviewAPI } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { toast } from 'sonner';

// Safe error message extraction
const getErrorMessage = (err) => {
    try {
        const detail = err?.response?.data?.detail;
        if (typeof detail === 'string') return detail;
        if (Array.isArray(detail)) return detail.map(e => e?.msg || e?.message || 'Error').join('. ');
        if (typeof detail === 'object') return detail?.msg || detail?.message || 'An error occurred';
        return err?.message || 'An error occurred';
    } catch {
        return 'An error occurred';
    }
};

const AdminDashboardHome = () => {
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [recentActivity, setRecentActivity] = useState([]);
    const [flaggedReviews, setFlaggedReviews] = useState([]);
    const [deletionRequests, setDeletionRequests] = useState([]);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [statsRes, pendingRes, deletionsRes] = await Promise.all([
                adminAPI.getStats(),
                adminAPI.getPendingReviews(),
                adminAPI.getDeletionRequests().catch(() => ({ data: [] }))
            ]);
            
            setStats(statsRes.data);
            setFlaggedReviews(pendingRes.data?.slice(0, 5) || []);
            setDeletionRequests(deletionsRes.data?.slice(0, 5) || []);
        } catch (err) {
            toast.error(getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <AdminLayout>
                <div className="flex justify-center items-center min-h-[60vh]">
                    <Loader2 className="h-12 w-12 animate-spin text-primary" />
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="p-6 lg:p-8 space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="font-heading font-extrabold text-2xl lg:text-3xl text-foreground">
                            Admin Dashboard
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Platform overview and operations center
                        </p>
                    </div>
                    <Button onClick={loadData} variant="outline" size="sm">
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Refresh
                    </Button>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4">
                    <Card className="bg-card border-border">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-primary/10">
                                    <Users className="h-5 w-5 text-primary" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-foreground tabular-nums">
                                        {stats?.total_users || 0}
                                    </p>
                                    <p className="text-xs text-muted-foreground">Total Users</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="bg-card border-border">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-emerald-500/10">
                                    <MapPin className="h-5 w-5 text-emerald-500" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-foreground tabular-nums">
                                        {stats?.total_addresses || 0}
                                    </p>
                                    <p className="text-xs text-muted-foreground">Addresses</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="bg-card border-border">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-blue-500/10">
                                    <FileText className="h-5 w-5 text-blue-500" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-foreground tabular-nums">
                                        {stats?.total_reviews || 0}
                                    </p>
                                    <p className="text-xs text-muted-foreground">Total Reviews</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="bg-card border-border">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-amber-500/10">
                                    <CheckCircle className="h-5 w-5 text-amber-500" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-foreground tabular-nums">
                                        {stats?.active_users || 0}
                                    </p>
                                    <p className="text-xs text-muted-foreground">Active Users</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Attention Required Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card className={`border-2 ${(stats?.flagged_reviews || 0) > 0 ? 'border-red-500/50 bg-red-500/5' : 'border-border'}`}>
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <AlertTriangle className={`h-6 w-6 ${(stats?.flagged_reviews || 0) > 0 ? 'text-red-500' : 'text-muted-foreground'}`} />
                                    <div>
                                        <p className="text-3xl font-bold text-foreground tabular-nums">
                                            {stats?.flagged_reviews || 0}
                                        </p>
                                        <p className="text-sm text-muted-foreground">Flagged Reviews</p>
                                        <p className="text-xs text-muted-foreground">Needs manual review</p>
                                    </div>
                                </div>
                                <Link to="/admin/moderation">
                                    <Button variant="outline" size="sm">
                                        Review <ArrowRight className="ml-1 h-4 w-4" />
                                    </Button>
                                </Link>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className={`border-2 ${(stats?.pending_deletion_requests || 0) > 0 ? 'border-purple-500/50 bg-purple-500/5' : 'border-border'}`}>
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <Trash2 className={`h-6 w-6 ${(stats?.pending_deletion_requests || 0) > 0 ? 'text-purple-500' : 'text-muted-foreground'}`} />
                                    <div>
                                        <p className="text-3xl font-bold text-foreground tabular-nums">
                                            {stats?.pending_deletion_requests || 0}
                                        </p>
                                        <p className="text-sm text-muted-foreground">Deletion Requests</p>
                                    </div>
                                </div>
                                <Link to="/admin/deletions">
                                    <Button variant="outline" size="sm">
                                        Review <ArrowRight className="ml-1 h-4 w-4" />
                                    </Button>
                                </Link>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Two Column Layout */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Pending Reviews */}
                    <Card className="bg-card border-border">
                        <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                                <CardTitle className="font-heading font-bold text-lg flex items-center gap-2">
                                    <AlertTriangle className="h-5 w-5 text-amber-500" />
                                    Reviews Needing Attention
                                </CardTitle>
                                <Link to="/admin/moderation">
                                    <Button variant="ghost" size="sm">View All</Button>
                                </Link>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {flaggedReviews.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <CheckCircle className="h-8 w-8 mx-auto mb-2 text-emerald-500" />
                                    <p>No reviews pending moderation</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {flaggedReviews.map((review) => (
                                        <div key={review.id} className="flex items-center justify-between p-3 bg-accent/30 rounded-lg border border-border">
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-foreground truncate text-sm">
                                                    {review.address_street || 'Unknown Address'}
                                                </p>
                                                <p className="text-xs text-muted-foreground truncate">
                                                    {review.contractor_name || 'Unknown'} • {formatDate(review.created_at)}
                                                </p>
                                            </div>
                                            <Badge className={
                                                review.status === 'flagged' ? 'bg-red-600' :
                                                review.status === 'pending' ? 'bg-amber-600' : 'bg-gray-600'
                                            }>
                                                {review.status}
                                            </Badge>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Deletion Requests */}
                    <Card className="bg-card border-border">
                        <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                                <CardTitle className="font-heading font-bold text-lg flex items-center gap-2">
                                    <Trash2 className="h-5 w-5 text-purple-500" />
                                    Pending Deletion Requests
                                </CardTitle>
                                <Link to="/admin/deletions">
                                    <Button variant="ghost" size="sm">View All</Button>
                                </Link>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {deletionRequests.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <CheckCircle className="h-8 w-8 mx-auto mb-2 text-emerald-500" />
                                    <p>No pending deletion requests</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {deletionRequests.map((req) => (
                                        <div key={req.id} className="flex items-center justify-between p-3 bg-accent/30 rounded-lg border border-border">
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-foreground truncate text-sm">
                                                    {req.company_name || req.user_email}
                                                </p>
                                                <p className="text-xs text-muted-foreground truncate">
                                                    {req.reason || 'No reason provided'} • {formatDate(req.requested_at)}
                                                </p>
                                            </div>
                                            <Badge className={
                                                req.status === 'pending' ? 'bg-amber-600' :
                                                req.status === 'approved' ? 'bg-emerald-600' : 'bg-gray-600'
                                            }>
                                                {req.status}
                                            </Badge>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Quick Actions */}
                <Card className="bg-card border-border">
                    <CardHeader className="pb-3">
                        <CardTitle className="font-heading font-bold text-lg">Quick Actions</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <Link to="/admin/reviews">
                                <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2">
                                    <FileText className="h-5 w-5" />
                                    <span className="text-xs">All Reviews</span>
                                </Button>
                            </Link>
                            <Link to="/admin/users">
                                <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2">
                                    <Users className="h-5 w-5" />
                                    <span className="text-xs">Manage Users</span>
                                </Button>
                            </Link>
                            <Link to="/admin/ownership">
                                <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2">
                                    <RefreshCw className="h-5 w-5" />
                                    <span className="text-xs">Ownership Reset</span>
                                </Button>
                            </Link>
                            <Link to="/admin/audit">
                                <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2">
                                    <Eye className="h-5 w-5" />
                                    <span className="text-xs">Audit Log</span>
                                </Button>
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </AdminLayout>
    );
};

export default AdminDashboardHome;
